// START IMPORTS
import { CookiesStorage } from '@ltvco/refresh-lib';
import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { FIRST_REPORT_SCREEN_KEY } from '@ltvco/refresh-lib/hooks';
import {
  ReactivatePausedAccountCta,
  useMonitorUpsell,
} from '@ltvco/refresh-lib/payments';
import {
  Text,
  Grid,
  Link,
  styled,
  CustomIcons,
  IconButton,
  Stack,
  useTheme,
  Box,
} from '@ltvco/refresh-lib/theme';
import {
  DateUtil,
  useScreenSize,
  useFirstRecordSearchRedirect,
  isUserInPhoneOnlyTest,
  type NormalizedRecordSearch,
} from '@ltvco/refresh-lib/utils';
import {
  closeSnackbar,
  MobileAppsCtaCard,
  NeedAssistanceSidebar,
  RightRailCard,
  useAccount,
  useSnackbar,
} from '@ltvco/refresh-lib/v1';
import {
  BackdropLoading,
  EmailSettingsCard,
  FeatureBlockedModal,
  MonitoredReportsModal,
  SideBarMigrationCta,
  useFeatureBlockedModal,
  MonitoringPlusUpsellCard,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { Security } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useState, useContext } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { INITIAL_SEARCH_KEY } from 'components/ChatBV/constants';
import { Sidenav } from 'components/sidenav/Sidenav';
import { SidenavContainer } from 'components/sidenav/SidenavContainer';
import { MonitorUpsellModalProvider } from 'context/MonitorUpsellModalContext';
import { buildRecallsRedirectUrl } from 'routes/utils/buildRecallsRedirectUrl';

// END IMPORTS

// START STYLED_COMPONENTS
const DollarsAndCoinsIcon = styled(CustomIcons.DollarsAndCoinsIcon)(
  ({ theme }) => ({
    fontSize: '3.75rem',
    padding: theme.spacing(0.9375),
  })
);

const StyledContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
  width: '100%',
  '&&': {
    '& > *': {
      width: '100%',
      maxWidth: '100%',
      border: `1px solid ${theme.palette.background.light}`,
      boxShadow: 'none',
      '& > button': {
        width: '95%',
      },
      '& > div button': {
        paddingRight: 0,
      },
    },
  },
}));

const StyledContent = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: 'auto',
  marginTop: `-${theme.spacing(1)}`,
  backgroundColor: theme.palette.background.default,

  [theme.breakpoints.up('md')]: {
    margin: 'auto',
  },

  [theme.breakpoints.up('lg')]: {
    margin: 'auto',
    maxWidth: '1100px',
  },
}));
// END STYLED_COMPONENTS

/** Delay (ms) before the escape link appears on the loading screen. Set to 0 for immediate. */
const ESCAPE_LINK_DELAY_MS: number = 20000;

export function DashboardParent({
  contactUsHandler,
  handleLogout,
  openConfirmCancelModal,
}: {
  contactUsHandler: () => void;
  handleLogout: () => void;
  // eslint-disable-next-line no-unused-vars
  openConfirmCancelModal: (downsellTest?: boolean) => void;
}) {
  // START HOOKS_AND_QUERIES
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: account } = useAccount(true);
  const { routingUtils } = useContext(AppConfig);
  const { isMobile } = useScreenSize();
  const isOnChatBVFlowVariation = useFeatureIsOn('prop-3502');
  const isOnChatBVFlowFooterVariation = useFeatureIsOn('prop-3640');
  const inUCMAddonVariation = useFeatureIsOn('RTRN-1526');
  const hasPurchasedUCMAddon =
    account?.account?.subscription_info?.one_time_ucm_purchased;
  const shouldDisplayUcmAddonModal =
    !!account && inUCMAddonVariation && !hasPurchasedUCMAddon;
  const { shouldShowMonitoringAddonUpsell } = useMonitorUpsell();
  const isInAnyChatBVFlowVariation =
    isOnChatBVFlowVariation || isOnChatBVFlowFooterVariation;
  const theme = useTheme();
  const { trackEventGA4, logError } = useContext(AppConfig);
  const {
    internationalLanguageVariation,
    vinVariation,
    isBlocked,
    isHidden,
    phoneOnlyVariation,
  } = useSubscriptionAccessConfigContext();
  const {
    isFeatureBlockedModalOpen,
    closeFeatureBlockedModal,
    openFeatureBlockedModal,
  } = useFeatureBlockedModal();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const cookieStorage = new CookiesStorage();
  const [searchType, setSearchType] = useState('');
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);

  const openBlockedFeatureModal = (nextSearchType: string): void => {
    setSearchType(nextSearchType);
    setPaymentShowModalNumber(0);
    openFeatureBlockedModal();
  };

  const openMonitoringUpsellConfirmModal = () => {
    openBlockedFeatureModal('Report Monitoring');
  };
  const [monitoredReportsOpen, setMonitoredReportsOpen] = useState(false);
  const [forceHideLoading, setForceHideLoading] = useState(false);
  const [showEscapeLink, setShowEscapeLink] = useState(
    ESCAPE_LINK_DELAY_MS === 0
  );
  // END STATE_MANAGEMENT

  // START FIRST_RECORD_SEARCH
  const isInNewLoadingScreenVariation = useFeatureIsOn('RFRSH-4239');
  const { showLoading: showFirstReportLoading, data: firstRecordSearchData } =
    useFirstRecordSearchRedirect({
      account,
      routingUtils,
      navigate,
      skipLoadingTypes: ['court', 'unclaimed_money', 'recalls'],
      beforeRedirect: (data: NormalizedRecordSearch) => {
        if (cookieStorage.getValue('flow_type') === 'ancestry') {
          navigate('/dashboard/family-ancestry');
          return false;
        }
        if (isInAnyChatBVFlowVariation) {
          const existingData = localStorage.getItem(INITIAL_SEARCH_KEY);
          const isOnDashboardRoute = location.pathname.startsWith('/dashboard');
          const isOnChatRoute = location.pathname.startsWith('/chatbv');
          if ((isOnDashboardRoute || isOnChatRoute) && !existingData) {
            try {
              localStorage.setItem(INITIAL_SEARCH_KEY, JSON.stringify(data));
            } catch (_error) {
              logError?.(
                'Failed to store initial search data',
                _error as Error
              );
            }
          }
          localStorage.removeItem('recordSearch');
          cookieStorage.removeValue('first_record_search');
          return false;
        }
      },
      afterRedirect: (data: NormalizedRecordSearch) => {
        if (isInNewLoadingScreenVariation) {
          sessionStorage.setItem('first_report_generating', data.type);
          localStorage.setItem(FIRST_REPORT_SCREEN_KEY, 'true');
        }
      },
      customHandlers: {
        recalls: buildRecallsRedirectUrl,
        personality_test: (data: NormalizedRecordSearch) => {
          const responseMap: Record<string, string> = {
            '0': 'Strongly Disagree',
            '1': 'Disagree',
            '2': 'Neutral',
            '3': 'Agree',
            '4': 'Strongly Agree',
          };
          const testAnswers = data.personalityTestResults ?? {};
          const answersToStore = Object.keys(testAnswers).map(
            (custom_attribute, index) => ({
              custom_attribute,
              response: responseMap[testAnswers[custom_attribute]],
              screen_id: `TRAIT-BV-01-${index + 1}`,
            })
          );
          localStorage.setItem(
            'answersStorage',
            JSON.stringify(answersToStore)
          );
          localStorage.setItem(
            'activeQuestionIndex',
            String(answersToStore.length)
          );
          return '/dashboard/personality-traits';
        },
      },
    });
  // END FIRST_RECORD_SEARCH

  // START DERIVED_DATA_AND_UTILS
  const dateUtil = new DateUtil();
  const currentDate = new Date();

  const featureAction = (feature: string) => {
    if (feature === 'Unclaimed Money' && shouldDisplayUcmAddonModal) {
      trackEventGA4({
        event_name: 'engagement_click',
        type: 'cta_dashboard_card',
        subtype: 'ucm_upsell',
      });
    }

    openBlockedFeatureModal(feature);
  };

  function closeMonitoredReportsModal() {
    setMonitoredReportsOpen(false);
  }

  function handleReactivationForPausedAccountsBanner() {
    if (!account) {
      return;
    }

    const isInPausedStatus = account?.meta?.subscription_state === 'paused';
    if (!isInPausedStatus) {
      return;
    }

    const today = currentDate.toISOString();
    const expirationDate = account?.account.subscription_info?.normalize_date;

    if (!expirationDate) {
      return;
    }

    const isAfterCurrentDate = dateUtil.isAfter(today, expirationDate);

    function handleReactivationClick() {
      closeSnackbar(pausedAccountSnackbarId);
      navigate('/reactivation');
    }

    const expiredMessage = (
      <Text>
        <b>
          Your account expired on{' '}
          {dateUtil.parseDateFromString(
            account?.account.subscription_info?.normalize_date,
            'M/d/y'
          )}{' '}
          . Reactivate to regain access.
        </b>
      </Text>
    );

    const cancelledMessage = (
      <Text>
        <b>
          Your account with member ID {account?.account?.user_info?.user_code}{' '}
          has been cancelled.
        </b>{' '}
        <br /> You have until{' '}
        {dateUtil.parseDateFromString(
          account?.account.subscription_info?.normalize_date,
          'M/d/y'
        )}{' '}
        to continue running reports. Please reactivate now to maintain access.
        Visit{' '}
        <Link
          color={'primary.contrastText'}
          onClick={handleReactivationClick}
          style={{ cursor: 'pointer' }}
        >
          here
        </Link>{' '}
        to reactivate.
      </Text>
    );

    const message = isAfterCurrentDate ? expiredMessage : cancelledMessage;

    const pausedAccountSnackbarId = enqueueSnackbar(
      <Stack direction="row" justifyContent="space-between">
        {message}
        {getCloseButton(() => {
          closeSnackbar(pausedAccountSnackbarId);
        })}
      </Stack>,
      {
        variant: 'info',
        persist: true,
        action: <></>,
      }
    );

    localStorage.setItem(
      'pausedAccountSnackbarId',
      JSON.stringify(pausedAccountSnackbarId)
    );
  }

  function getCloseButton(onClick: () => void) {
    return (
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={onClick}
        sx={{ marginLeft: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    );
  }

  function handleSiteNotifications() {
    if (!account) {
      return;
    }
    const siteNotification = account?.meta?.notification;
    if (!siteNotification) {
      return;
    }
    const siteNotificationSnackbarId = enqueueSnackbar(
      <Text>
        <b>{siteNotification.title}</b> <br /> {siteNotification.message}
      </Text>,
      {
        variant: 'info',
        persist: true,
        action: (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              closeSnackbar(siteNotificationSnackbarId);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      }
    );
  }

  const handleFraudScanClick = () => {
    trackEventGA4({
      event_name: 'engagement_click',
      type: 'fraud_cta',
      section: 'fraudscan',
    });
    isBlocked('fraud_scan') ? featureAction('Fraud Scan') : navigate('fraud');
  };

  const isIdMonitorIndex = location.pathname === '/dashboard/id-monitor';
  const isMonitoringPage = location.pathname.includes('/dashboard/monitoring');
  const isBillingPage = location.pathname.includes('/dashboard/billing');
  const isReportsPage = location.pathname.includes('/dashboard/reports');
  const isDarkWebPage = location.pathname.includes('/dashboard/dark-web');

  const showRightRail = isMonitoringPage || isBillingPage || isReportsPage;

  const appsFlyerLink = 'https://beenverified.onelink.me/5mdJ/j0m5b8cj';

  const { phoneOnlyPlanNames } = constants;
  const isPhoneOnlyVariation =
    isUserInPhoneOnlyTest(account, phoneOnlyPlanNames) || phoneOnlyVariation;

  const shouldShowMobileAppsCta =
    isReportsPage &&
    !vinVariation &&
    !internationalLanguageVariation &&
    !isPhoneOnlyVariation;

  const ucmSubtitle =
    inUCMAddonVariation && !hasPurchasedUCMAddon
      ? 'There’s an estimated $70 billion worth of unclaimed money in the United States'
      : 'Included with your membership is a state government search of billions in unclaimed money';
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (localStorage.getItem('pausedAccountSnackbarId')) {
      try {
        closeSnackbar(
          JSON.parse(localStorage.getItem('pausedAccountSnackbarId') || '')
        );
      } catch (_e) {
        // Error parsing pausedAccountSnackbarId from localStorage
      }
    }
    handleSiteNotifications();
    handleReactivationForPausedAccountsBanner();
  }, [account]);

  // Store chatBVInitialSearch immediately when we have firstRecordSearchData
  // This is for DeepSearch/ChatBV flow only
  // Only store if user is in the prop-3502 variation

  useEffect(() => {
    if (
      !isInAnyChatBVFlowVariation &&
      localStorage.getItem(INITIAL_SEARCH_KEY)
    ) {
      localStorage.removeItem(INITIAL_SEARCH_KEY);
    }
  }, [isInAnyChatBVFlowVariation]);

  useEffect(() => {
    if (
      !showFirstReportLoading ||
      forceHideLoading ||
      ESCAPE_LINK_DELAY_MS === 0
    ) {
      return;
    }
    const timer = setTimeout(
      () => setShowEscapeLink(true),
      ESCAPE_LINK_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [showFirstReportLoading, forceHideLoading]);

  const handleEscapeLoading = useCallback(() => {
    trackEventGA4({
      event_name: 'report_element_engagement',
      type: 'escape_link',
      report_type: firstRecordSearchData?.type ?? 'unknown',
      interacted_from: 'loading_screen',
    });
    localStorage.removeItem(INITIAL_SEARCH_KEY);
    setForceHideLoading(true);
  }, [firstRecordSearchData?.type, trackEventGA4]);
  // END EFFECTS

  const handleMonitoringUpsellBillingSidebarClick = (): void => {
    trackEventGA4({
      event_name: 'engagement_click',
      type: 'cta_my_account',
      section_name: 'monitoring_upsell',
    });
    openBlockedFeatureModal('Report Monitoring');
  };

  const handleMonitoringUnlockClick = (): void => {
    if (searchType === 'Report Monitoring') {
      trackEventGA4({
        event_name: 'engagement_click',
        type: 'confirmation_unlock',
        section_name: 'monitoring_upsell',
      });
    }
  };

  const handleMonitoringLegalAccepted = (): void => {
    if (searchType === 'Report Monitoring') {
      trackEventGA4({
        event_name: 'engagement_click',
        type: 'last_step_unlock',
        section_name: 'monitoring_upsell',
      });
    }
  };

  // START DATA_PREPARATION
  const RightRailComponent = (
    <Grid
      container
      size={{ xs: 12, md: 3, lg: 3 }}
      spacing={1}
      direction="column"
      marginTop={2}
      maxWidth={{ xs: '100%', md: '275px' }}
    >
      {isBillingPage && (
        <Grid
          width={'100%'}
          order={{ xs: 6, md: 1 }}
          className="needs-assistance"
        >
          <NeedAssistanceSidebar
            logout={handleLogout}
            supportImgUrl={constants.images.img24hr}
            contactUsHandler={contactUsHandler}
            openConfirmCancelModal={openConfirmCancelModal}
          />
          {shouldShowMonitoringAddonUpsell && (
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <MonitoringPlusUpsellCard
                sx={{ flexShrink: 0 }}
                onUpgradeClick={handleMonitoringUpsellBillingSidebarClick}
              />
            </Box>
          )}
        </Grid>
      )}
      {isMonitoringPage && (
        <Grid width={'100%'} order={2}>
          <EmailSettingsCard
            currentInstantAlertsSetting={
              account?.account?.alert_me.settings.instant_enabled || false
            }
            currentDigestAlertsSetting={
              account?.account?.alert_me.settings.digest_enabled || false
            }
            handleMonitoredReports={() => setMonitoredReportsOpen(true)}
          />
          <MonitoredReportsModal
            isOpen={monitoredReportsOpen}
            onClose={() => closeMonitoredReportsModal()}
          />
        </Grid>
      )}
      {!isMonitoringPage &&
        !isBillingPage &&
        !vinVariation &&
        !internationalLanguageVariation && (
          <>
            {isMobile && (
              <Grid width={'100%'} order={2} key="mobile-migration-card">
                <StyledContainer>
                  <SideBarMigrationCta />
                </StyledContainer>
              </Grid>
            )}
            <Grid width={'100%'} order={3}>
              {!isHidden('unclaimed_money') && (
                <RightRailCard
                  icon={<DollarsAndCoinsIcon />}
                  title="Check for Unclaimed Money"
                  subtitle={ucmSubtitle}
                  body="Find out if unclaimed money is owed to you or anyone you know!"
                  buttonText="Unclaimed Money Search"
                  tagText="Bonus Feature"
                  showTag={true}
                  tagOnLeft={false}
                  onButtonClick={() =>
                    isBlocked('unclaimed_money') || shouldDisplayUcmAddonModal
                      ? featureAction('Unclaimed Money')
                      : navigate('unclaimed-money')
                  }
                />
              )}
            </Grid>
            <Grid width={'100%'} order={4}>
              {!isHidden('fraud_scan') && (
                <RightRailCard
                  icon={
                    <Security
                      sx={{
                        fontSize: '3.75rem',
                        padding: 1,
                        color: theme.palette.primary.main,
                      }}
                    />
                  }
                  title="FraudScan"
                  subtitle="Stop fraud fast! Phone, email, IP, and URL scans now available"
                  body="Spot risks, flag suspicious activity, and stay ahead of fraud with powerful, easy-to-use lookups"
                  buttonText="Scan Now"
                  tagText="New"
                  showTag={true}
                  tagOnLeft={false}
                  onButtonClick={handleFraudScanClick}
                />
              )}
            </Grid>
          </>
        )}
      {shouldShowMobileAppsCta && (
        <Grid width={'100%'} order={5}>
          <MobileAppsCtaCard appsFlyerLink={appsFlyerLink} />
        </Grid>
      )}
      {account?.meta?.subscription_state === 'paused' && (
        <Grid order={5}>
          <ReactivatePausedAccountCta />
        </Grid>
      )}
      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType={searchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
        onUnlockClick={handleMonitoringUnlockClick}
        onLegalAccepted={handleMonitoringLegalAccepted}
      />
    </Grid>
  );

  const RightRail = showRightRail ? RightRailComponent : <></>;

  if (isDarkWebPage) {
    return (
      <StyledContent>
        <Sidenav onReport />
        <Grid container>
          <Grid size={12} maxWidth="100%">
            <Outlet />
          </Grid>
        </Grid>
      </StyledContent>
    );
  }
  // END DATA_PREPARATION

  // START RENDER
  return (
    <MonitorUpsellModalProvider
      openMonitoringUpsellConfirmModal={openMonitoringUpsellConfirmModal}
    >
      <SidenavContainer defaultBackground={isIdMonitorIndex}>
        <Grid container justifyContent={'space-between'} height="100%">
          <Grid
            size={{
              xs: 12,
              md: showRightRail ? 8 : 12,
              lg: showRightRail ? 8 : 12,
            }}
            maxWidth={{
              xs: '100%',
              md: showRightRail ? '911px !important' : '100%',
            }}
          >
            {showFirstReportLoading && !isInNewLoadingScreenVariation ? (
              <BackdropLoading
                message={
                  <Stack spacing={2} alignItems="center">
                    <Text variant="h5">Generating first report</Text>
                    {showEscapeLink && (
                      <Link
                        color="primary"
                        onClick={handleEscapeLoading}
                        sx={{
                          cursor: 'pointer',
                          background: 'none',
                          border: 0,
                          padding: 0,
                        }}
                        underline="always"
                        type="button"
                      >
                        Skip to dashboard
                      </Link>
                    )}
                  </Stack>
                }
                sx={{ backgroundColor: 'white' }}
              />
            ) : (
              <Outlet />
            )}
          </Grid>
          {RightRail}
        </Grid>
      </SidenavContainer>
    </MonitorUpsellModalProvider>
  );
  // END RENDER
}
